/**
 * AnalyticsProvider — global operator intelligence pipe (Phase 178.6.E).
 *
 * DESIGN DOCTRINE
 * ───────────────
 * Single canonical surface for all UI interaction telemetry.
 *
 *   • emit-sites call `track(event, payload)` from `@/lib/analytics`
 *   • THIS provider installs `window.__OUTPLAY_TRACK__` — the sink the
 *     emitter forwards to
 *   • THIS provider auto-enriches every event with capability context:
 *     user_id, operator_role, capabilities, city, academy_id, venue_id,
 *     organizer_id, theme, device, viewport — so emit-sites NEVER have to
 *     repeat them
 *   • batches events with 50ms debounce + 500ms flush ceiling, buffers
 *     offline (when navigator.onLine is false), flushes on online resume
 *   • vendor-neutral by default.  Provider noops cleanly when no keys are
 *     configured.  When `REACT_APP_POSTHOG_KEY` (or MIXPANEL_TOKEN) is
 *     set, the corresponding SDK is loaded lazily via dynamic import so
 *     the analytics-free user pays zero bundle cost.
 *
 * RELIABILITY GUARANTEES
 * ──────────────────────
 *   • NEVER throws — all errors swallowed at every layer
 *   • NEVER blocks UI thread — dispatch via queueMicrotask
 *   • NEVER blocks navigation — fire-and-forget
 *   • NEVER captures payment/auth field values — privacy doctrine
 *   • Buffer capped at 1000 events (FIFO drop) so memory can't grow unbounded
 *
 * EXPORTS
 * ───────
 *   <AnalyticsProvider>       — mount at app root (inside AuthProvider)
 *   useAnalytics()            — hook for components needing track/identify
 *   identify(user_id, traits) — explicit identity binding (called from login)
 *   resetAnalytics()          — clear identity (called from logout)
 *
 * The `track()` function in `@/lib/analytics` is the public emit API; this
 * provider is the SINK it pipes to.  Both layers are independently testable.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useArenaxCapabilities } from '@/lib/arenaxCapabilities';

const AnalyticsContext = createContext(null);

const MAX_BUFFER = 1000;
const FLUSH_DEBOUNCE_MS = 50;
const FLUSH_CEILING_MS = 500;

// ── Privacy doctrine — fields stripped from every payload before send ──
// We never let secrets/payment fields leak into analytics, even by accident.
const SENSITIVE_KEYS = [
  'password', 'token', 'access_token', 'refresh_token', 'jwt',
  'razorpay_signature', 'razorpay_order_id', 'razorpay_payment_id',
  'card', 'cvv', 'card_number', 'card_cvv', 'card_expiry',
  'pan', 'aadhar', 'aadhaar', 'otp',
];

function sanitizePayload(p) {
  if (!p || typeof p !== 'object') return p;
  const out = {};
  for (const [k, v] of Object.entries(p)) {
    const kl = String(k).toLowerCase();
    if (SENSITIVE_KEYS.some((s) => kl.includes(s))) continue;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = sanitizePayload(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function deviceType() {
  if (typeof window === 'undefined') return 'server';
  const ua = navigator.userAgent || '';
  if (/Mobi|Android|iPhone|iPad/.test(ua)) return /iPad/.test(ua) ? 'tablet' : 'mobile';
  return 'desktop';
}

export function AnalyticsProvider({ children }) {
  const { user } = useAuth() || {};
  const capCtx = useArenaxCapabilities() || {};
  // Pull capability metadata.  This wires every event with the current
  // identity envelope without forcing emit-sites to know about caps.
  const capabilities = useMemo(
    () => (Array.isArray(capCtx?.capabilities) ? capCtx.capabilities : []),
    [capCtx?.capabilities]
  );

  // Sinks — installed once.  Vendor SDK refs are lazy + nullable so the
  // analytics-free path is zero-cost.
  const posthogRef = useRef(null);
  const mixpanelRef = useRef(null);
  const queueRef = useRef([]);
  const flushTimerRef = useRef(null);
  const flushCeilingRef = useRef(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  // Build the identity envelope.  Memoised so we don't rebuild it on every
  // event — keeps emit-time hot path tight.
  const envelope = useMemo(() => ({
    user_id: user?.id || null,
    operator_role: user?.role || 'guest',
    capabilities,
    city: user?.city || user?.academy?.city || null,
    academy_id: user?.academy_id || null,
    venue_id: user?.venue_id || null,
    organizer_id: user?.organizer_id || null,
    theme: (typeof document !== 'undefined'
              ? document.documentElement.getAttribute('data-theme')
              : null),
    device: deviceType(),
    viewport: (typeof window !== 'undefined'
                ? `${window.innerWidth}x${window.innerHeight}`
                : null),
  }), [
    user?.id, user?.role, user?.city, user?.academy?.city,
    user?.academy_id, user?.venue_id, user?.organizer_id,
    capabilities,
  ]);

  // Forward a single sanitized batch to whatever vendor SDK is configured.
  const flushNow = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    if (flushCeilingRef.current) {
      clearTimeout(flushCeilingRef.current);
      flushCeilingRef.current = null;
    }
    if (!queueRef.current.length) return;
    if (!isOnline) return;          // offline — keep queueing
    const batch = queueRef.current.splice(0, queueRef.current.length);
    // Forward to vendor SDKs (if configured).  Each in try/catch so a sick
    // sink can't poison the others.
    for (const evt of batch) {
      try {
        if (posthogRef.current?.capture) {
          posthogRef.current.capture(evt.event, evt.payload);
        }
      } catch (_) { /* swallow */ }
      try {
        if (mixpanelRef.current?.track) {
          mixpanelRef.current.track(evt.event, evt.payload);
        }
      } catch (_) { /* swallow */ }
    }
  }, [isOnline]);

  // Schedule a debounced flush + hard ceiling so events can't sit forever.
  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(flushNow, FLUSH_DEBOUNCE_MS);
    if (!flushCeilingRef.current) {
      flushCeilingRef.current = setTimeout(flushNow, FLUSH_CEILING_MS);
    }
  }, [flushNow]);

  // The sink installed on `window.__OUTPLAY_TRACK__`.  `track()` in
  // `@/lib/analytics` calls this; we enrich + queue + schedule.
  const sink = useCallback((event, payload) => {
    try {
      const enriched = {
        ...envelope,
        ...sanitizePayload(payload || {}),
        emitted_at: new Date().toISOString(),
      };
      queueRef.current.push({ event, payload: enriched });
      if (queueRef.current.length > MAX_BUFFER) {
        queueRef.current.splice(0, queueRef.current.length - MAX_BUFFER);
      }
      scheduleFlush();
    } catch (_) { /* swallow */ }
  }, [envelope, scheduleFlush]);

  // Install the sink on window so `@/lib/analytics` can forward to it.
  // We replace any previous sink — the provider is the canonical owner.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.__OUTPLAY_TRACK__ = sink;
    return () => {
      if (window.__OUTPLAY_TRACK__ === sink) {
        delete window.__OUTPLAY_TRACK__;
      }
    };
  }, [sink]);

  // Online/offline tracking so we don't bleed events into the void.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onOnline = () => { setIsOnline(true); scheduleFlush(); };
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [scheduleFlush]);

  // Lazy vendor SDK boot.
  //
  // Phase 178.6.I Commit F: PostHog now loads via ES module
  // (`@/lib/posthog_init`) which applies the full privacy masking
  // doctrine.  The legacy script-tag polling stays as a fallback for
  // deployments that prefer the snippet pattern.
  useEffect(() => {
    const phKey = process.env.REACT_APP_POSTHOG_KEY;
    if (!phKey || phKey === 'placeholder' || typeof window === 'undefined') return undefined;
    let cancelled = false;
    // ES module path — privacy-masked, operator-only replay
    import('@/lib/posthog_init').then(({ initPostHog }) => {
      if (cancelled) return;
      return initPostHog(user);
    }).then((ph) => {
      if (cancelled || !ph) return;
      posthogRef.current = ph;
      flushNow();
    }).catch(() => { /* non-fatal */ });
    // Script-tag fallback poll (still allowed)
    let tries = 0;
    const probe = setInterval(() => {
      tries += 1;
      if (typeof window.posthog === 'object' && window.posthog?.capture
          && posthogRef.current !== window.posthog) {
        posthogRef.current = window.posthog;
        clearInterval(probe);
        flushNow();
      } else if (tries > 30) {
        clearInterval(probe);
      }
    }, 100);
    return () => { cancelled = true; clearInterval(probe); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushNow, user?.id, user?.role]);

  useEffect(() => {
    const mxToken = process.env.REACT_APP_MIXPANEL_TOKEN;
    if (!mxToken || typeof window === 'undefined') return undefined;
    let tries = 0;
    const probe = setInterval(() => {
      tries += 1;
      if (typeof window.mixpanel === 'object' && window.mixpanel?.track) {
        mixpanelRef.current = window.mixpanel;
        clearInterval(probe);
        flushNow();
      } else if (tries > 30) {
        clearInterval(probe);
      }
    }, 100);
    return () => clearInterval(probe);
  }, [flushNow]);

  // Identity binding — explicit, called from login flows.
  const identify = useCallback((userId, traits = {}) => {
    try {
      const safeTraits = sanitizePayload(traits);
      try { posthogRef.current?.identify?.(userId, safeTraits); } catch (_) { /* swallow */ }
      try { mixpanelRef.current?.identify?.(userId); mixpanelRef.current?.people?.set?.(safeTraits); } catch (_) { /* swallow */ }
    } catch (_) { /* swallow */ }
  }, []);

  const resetAnalytics = useCallback(() => {
    try { posthogRef.current?.reset?.(); } catch (_) { /* swallow */ }
    try { mixpanelRef.current?.reset?.(); } catch (_) { /* swallow */ }
    queueRef.current = [];
  }, []);

  // Auto-identify whenever the user changes (login / logout).
  useEffect(() => {
    if (user?.id) {
      identify(user.id, {
        name: user.name,
        email: user.email,
        role: user.role,
      });
    }
  }, [user?.id, user?.name, user?.email, user?.role, identify]);

  const value = useMemo(() => ({
    track: sink,
    identify,
    resetAnalytics,
    isOnline,
    queueLength: () => queueRef.current.length,
  }), [sink, identify, resetAnalytics, isOnline]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  return useContext(AnalyticsContext);
}
